import * as React from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

// Flat calm surface: 1px border, card bg, shadow-sm; interactive cards lift to
// shadow-md on hover. No gradients, textures, or imagery (design system).
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  interactive?: boolean;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border border-border bg-card text-card-foreground shadow-sm transition-shadow",
        interactive && "cursor-pointer hover:shadow-md",
        className
      )}
      {...props}
    />
  )
);
Card.displayName = "Card";

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-5 py-4", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CHIP_TINT = {
  primary: "bg-primary/10 text-primary",
  green: "bg-status-green-bg text-status-green",
  yellow: "bg-status-yellow-bg text-status-yellow",
  red: "bg-status-red-bg text-status-red",
} as const;

// Full-round 40px chip holding a 20px stroke icon on a soft tint.
export function IconChip({
  icon: Icon,
  tint = "primary",
  className,
}: {
  icon: LucideIcon;
  tint?: keyof typeof CHIP_TINT;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
        CHIP_TINT[tint],
        className
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={2} />
    </div>
  );
}
